import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, Facebook, Twitter, Instagram, Linkedin, Youtube, ArrowRight } from 'lucide-react';
import { institutionInfo, applicationFormUrl, prospectusUrl } from '../../data/mock';

const Footer = () => {
  const quickLinks = [
    { name: 'Admissions', path: '/admissions' },
    { name: 'Programmes', path: '/programmes' },
    { name: 'Apply Now', path: applicationFormUrl, external: true },
    { name: 'Contact Us', path: '/contact' },
  ];

  const departments = [
    { name: 'Health & Allied Sciences', path: '/departments/health-allied-sciences' },
    { name: 'Business, Tourism & Planning', path: '/departments/business-tourism-planning' },
    { name: 'Science & Allied Technologies', path: '/departments/science-allied-technologies' },
  ];

  const resources = [
    { name: 'Student Portal', path: '#' },
    { name: 'Library', path: '#' },
    { name: 'Academic Calendar', path: '#' },
    { name: 'Download Prospectus', path: prospectusUrl, external: true },
    { name: 'Admin Portal', path: '/admin' },
  ];

  const relatedLinks = [
    { name: 'MoEST', url: 'https://www.moe.go.tz' },
    { name: 'HESLB', url: 'https://www.heslb.go.tz' },
    { name: 'NACTVET', url: 'https://www.nactvet.go.tz' },
    { name: 'NECTA', url: 'https://www.necta.go.tz' },
    { name: 'IUCEA', url: 'https://www.iucea.org' },
    { name: 'AAU', url: 'https://www.aau.org' },
    { name: 'AfriQAN', url: 'https://www.afriqan.aau.org' },
    { name: 'UNESCO', url: 'https://www.unesco.org' },
    { name: 'Ministry of Health', url: 'https://www.moh.go.tz' },
  ];

  return (
    <footer className="bg-[#142942] text-white">
      {/* Main Footer */}
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Institution Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_bit-tanzania/artifacts/nrm99k8q_BIT%20LOGO.png" 
                alt="BIT Logo" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h3 className="font-display font-bold text-lg">Baobab Institute</h3>
                <p className="text-[#d4a855] text-xs">of Tanzania</p>
              </div>
            </div>
            <p className="text-[#94a3b8] text-sm leading-relaxed mb-4">
              {institutionInfo.address}
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-[#d4a855] flex-shrink-0" />
                <span className="text-[#94a3b8]">{institutionInfo.email}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Globe className="w-4 h-4 text-[#d4a855] flex-shrink-0" />
                <a 
                  href={`https://${institutionInfo.website}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#94a3b8] hover:text-white transition-colors"
                >
                  {institutionInfo.website}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Globe className="w-4 h-4 text-[#d4a855] flex-shrink-0" />
                <a 
                  href={`https://${institutionInfo.vocationalCenter}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#94a3b8] hover:text-white transition-colors"
                >
                  {institutionInfo.vocationalCenter} <span className="text-[#d4a855] text-xs">(Vocational Center)</span>
                </a>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Phone className="w-4 h-4 text-[#d4a855] flex-shrink-0 mt-1" />
                <div className="text-[#94a3b8]">
                  {institutionInfo.phones.map((phone, idx) => (
                    <span key={idx} className="block">{phone}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display font-semibold text-lg mb-6 text-[#d4a855]">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  {link.external ? (
                    <a
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#94a3b8] hover:text-white transition-colors flex items-center gap-2 group"
                    >
                      <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.path}
                      className="text-[#94a3b8] hover:text-white transition-colors flex items-center gap-2 group"
                    >
                      <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Departments */}
          <div>
            <h4 className="font-display font-semibold text-lg mb-6 text-[#d4a855]">Departments</h4>
            <ul className="space-y-3">
              {departments.map((dept) => (
                <li key={dept.name}>
                  <Link
                    to={dept.path}
                    className="text-[#94a3b8] hover:text-white transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {dept.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-display font-semibold text-lg mb-6 text-[#d4a855]">Resources</h4>
            <ul className="space-y-3">
              {resources.map((resource) => (
                <li key={resource.name}>
                  {resource.external ? (
                    <a
                      href={resource.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#94a3b8] hover:text-white transition-colors flex items-center gap-2 group"
                    >
                      <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                      {resource.name}
                    </a>
                  ) : (
                    <Link
                      to={resource.path}
                      className="text-[#94a3b8] hover:text-white transition-colors flex items-center gap-2 group"
                    >
                      <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                      {resource.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* Social Icons */}
            <div className="mt-8">
              <h5 className="text-sm font-medium mb-4">Follow Us</h5>
              <div className="flex gap-3">
                {[Facebook, Twitter, Instagram, Linkedin, Youtube].map((Icon, index) => (
                  <a
                    key={index}
                    href="#"
                    className="w-9 h-9 bg-[#1e3a5f] rounded-full flex items-center justify-center hover:bg-[#d4a855] hover:text-[#142942] transition-all"
                    aria-label="Social media"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related Links Section */}
      <div className="border-t border-[#1e3a5f]">
        <div className="container-custom py-6">
          <h4 className="font-display font-semibold text-sm mb-4 text-[#d4a855] text-center">Related Links</h4>
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8">
            {relatedLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#94a3b8] text-sm hover:text-white transition-colors"
              >
                {link.name}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#1e3a5f]">
        <div className="container-custom py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#94a3b8] text-sm text-center md:text-left">
            © {new Date().getFullYear()} Baobab Institute of Tanzania. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-[#94a3b8]">
            <Link to="#" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="#" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
